import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumPrimary,
  ButtonMediumSecondary,
  GenericInput,
  Icon,
  InputIconPosition,
  InputLabelOrientation,
  InputState,
  type InputValidationState,
  Tabs,
  tabPanelId,
} from "@studio3t/generic-components";
import { useEffect, useId, useMemo, useState } from "react";
import styled from "styled-components";
import { JsonViewer } from "@/components/JsonViewer";
import {
  MODAL_Z_INDEX,
  ModalBackdrop,
  ModalCard,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalTitle,
} from "@/components/ModalChrome";
import { ModalCloseButton } from "@/components/ModalCloseButton";
import { useScripts, useServices } from "@/hooks/useApi";
import { useModalFocus } from "@/hooks/useModalFocus";
import { SelectDropdown } from "@/pages/AddService/SelectDropdown";
import { FormGroup, Label } from "@/pages/AddService/styles";
import { rhaiHighlightStyle, rhaiLanguage } from "./rhaiLanguage";
import {
  defaultSaveTab,
  scriptFilenameProblem,
  serviceNameProblem,
} from "./saveScriptModel";

const MODAL = designVariables.ui.modal;
const FORM = designVariables.ui.form;
const SEMANTIC = designVariables.ui.semantic;
const BUTTON = designVariables.ui.button;
const H5 = designVariables.type.heading.h5;

type SaveTab = "existing" | "new";

interface SaveScriptModalProps {
  isOpen: boolean;
  /** The draft the save persists — previewed read-only below the form. */
  script: string;
  /** Prefills the filename (the untitled row's pending name). */
  suggestedName: string;
  onClose: () => void;
  /** Both resolve an error message to surface, or null on success. */
  onSaveExisting: (serviceName: string, name: string) => Promise<string | null>;
  onSaveNewService: (
    serviceName: string,
    name: string,
  ) => Promise<string | null>;
}

// All of the modal's field state and behavior: fresh-per-open reset, the
// per-tab validations and the validate-save-close submit flow — the
// components below stay presentational (the NameModal recipe).
function useSaveScriptForm(props: SaveScriptModalProps) {
  const { isOpen, suggestedName, onClose } = props;
  const { data: services } = useServices();
  const udfServices = useMemo(
    () => (services ?? []).filter((s) => s.provider === "udf"),
    [services],
  );
  const [tab, setTabState] = useState<SaveTab>("existing");
  const [serviceName, setServiceName] = useState("");
  const [newServiceName, setNewServiceName] = useState("");
  const [filename, setFilename] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Fresh form per open: the suggested filename when it is one (a default
  // "Untitled script N" row name is not — the field then starts empty,
  // showing the placeholder rather than an instant error), the first UDF
  // service selected, and the tab that has anything to pick. The service
  // list settling later must not stomp typed state.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on open only
  useEffect(() => {
    if (!isOpen) return;
    setTabState(defaultSaveTab(udfServices.length));
    setServiceName(udfServices[0]?.name ?? "");
    setNewServiceName("");
    setFilename(
      scriptFilenameProblem(suggestedName, []) === null ? suggestedName : "",
    );
    setError(null);
    setSaving(false);
  }, [isOpen, suggestedName]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  // Duplicate check against the CHOSEN service's scripts — not the first
  // service's (the pre-modal behavior's blind spot). Save waits for the
  // list: with it still loading, or failed, a clashing filename would
  // pass the check here and only fail (or overwrite) at the engine.
  const checksDuplicates = tab === "existing" && serviceName !== "";
  const chosenScripts = useScripts(checksDuplicates ? serviceName : "");
  const duplicatesPending = checksDuplicates && chosenScripts.isPending;
  const duplicatesUnavailable = checksDuplicates && chosenScripts.isError;

  const filenameProblem = scriptFilenameProblem(
    filename,
    checksDuplicates ? (chosenScripts.data ?? []).map((s) => s.filename) : [],
  );
  const newServiceProblem = serviceNameProblem(
    newServiceName.trim(),
    (services ?? []).map((s) => s.name),
  );
  const serviceProblem =
    tab === "new" ? newServiceProblem : serviceName ? null : "Select a service";
  const canSubmit =
    !saving &&
    !duplicatesPending &&
    !duplicatesUnavailable &&
    filename.trim() !== "" &&
    filenameProblem === null &&
    serviceProblem === null;
  // A failed scripts fetch is the user's problem to see: the filename
  // can't be checked, so Save stays off until another service is picked
  // or the list loads.
  const shownError =
    error ??
    (duplicatesUnavailable
      ? `Couldn't load the scripts of "${serviceName}" to check the filename. Pick another service or try again.`
      : null);

  const setTab = (next: SaveTab) => {
    setTabState(next);
    setError(null);
  };

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const message =
        tab === "existing"
          ? await props.onSaveExisting(serviceName, filename.trim())
          : await props.onSaveNewService(
              newServiceName.trim(),
              filename.trim(),
            );
      if (message) {
        setError(message);
        return;
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save script");
    } finally {
      setSaving(false);
    }
  };

  return {
    udfServices,
    tab,
    setTab,
    serviceName,
    setServiceName,
    newServiceName,
    setNewServiceName,
    filename,
    setFilename,
    filenameProblem,
    serviceProblem,
    error: shownError,
    saving,
    canSubmit,
    submit,
  };
}

type SaveScriptForm = ReturnType<typeof useSaveScriptForm>;

// An error verdict shown only once the field holds text — an untouched
// field stays calm.
function typedProblem(
  value: string,
  problem: string | null,
): InputValidationState | undefined {
  if (value.trim() === "" || problem === null) return undefined;
  return { state: InputState.Error, messages: [problem] };
}

// The Existing tab's service picker (Figma Frame 830: a stacked label over
// a full-width secondary button with a Folder leading icon) / the New
// tab's service name input (the same Folder icon leading the text).
function ServiceField({ form }: { form: SaveScriptForm }) {
  if (form.tab === "existing") {
    return (
      <FormGroup>
        <Label as="span">Choose an existing service</Label>
        <SelectDropdown
          value={form.serviceName}
          options={form.udfServices.map((s) => ({
            value: s.name,
            label: s.name,
          }))}
          onChange={form.setServiceName}
          disabled={form.saving}
          placeholder="Select a service"
          leadingIcon={Icon.Folder}
          // The menu portals to the body: without this it opens under the
          // modal backdrop and never shows.
          floatingZIndex={MODAL_Z_INDEX + 1}
        />
      </FormGroup>
    );
  }
  return (
    <GenericInput
      label={{ text: "Service name", orientation: InputLabelOrientation.Top }}
      icon={{
        variant: Icon.Folder,
        size: BUTTON.iconsize,
        position: InputIconPosition.Leading,
      }}
      value={form.newServiceName}
      placeholder="e.g. email-masker"
      onChange={(e) => form.setNewServiceName(e.target.value)}
      disabled={form.saving}
      state={typedProblem(form.newServiceName, form.serviceProblem)}
    />
  );
}

function FilenameField({ form }: { form: SaveScriptForm }) {
  return (
    <GenericInput
      label={{
        text: "Script filename",
        orientation: InputLabelOrientation.Top,
      }}
      value={form.filename}
      placeholder="e.g. transform.rhai"
      onChange={(e) => form.setFilename(e.target.value)}
      disabled={form.saving}
      state={typedProblem(form.filename, form.filenameProblem)}
    />
  );
}

// The Save-script modal (Figma Modal demo 15365-114957 / 15365-139481):
// a brand-new script picks its home here. The Existing tab saves into a
// chosen UDF service, the New tab creates a service carrying the script
// (one engine call — it writes the file and namespaces the directory
// itself). Below the two-column form, a "Script preview" heading and the
// read-only CodeMirror viewer, full-bleed under a hairline like the Raw
// configuration modal's, show exactly what will be saved.
export function SaveScriptModal(props: SaveScriptModalProps) {
  const { isOpen, script, onClose } = props;
  const modalRef = useModalFocus(isOpen);
  const idBase = useId();
  const form = useSaveScriptForm(props);

  if (!isOpen) return null;

  return (
    <ModalBackdrop onClick={onClose} role="presentation">
      <Card
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        data-testid="save-script-modal"
      >
        <ModalHeader>
          <ModalTitle>Save script</ModalTitle>
          <ModalCloseButton
            $top={MODAL.header.paddingh}
            $right={MODAL.paddingw}
            onClick={onClose}
            aria-label="Close modal"
          />
        </ModalHeader>

        <Tabs
          idBase={idBase}
          label="Where to save the script"
          activeId={form.tab}
          onChange={(id) => form.setTab(id as SaveTab)}
          tabs={[
            { id: "existing", label: "Existing service" },
            { id: "new", label: "New service" },
          ]}
        />

        <Body id={tabPanelId(idBase, form.tab)} role="tabpanel">
          <FieldRow>
            <ServiceField form={form} />
            <FilenameField form={form} />
          </FieldRow>
          {form.error && <ErrorLine role="alert">{form.error}</ErrorLine>}
          <PreviewHeading>Script preview</PreviewHeading>
        </Body>

        <PreviewFrame>
          {/* The viewer's Copy menu portals to the body, like the service
              picker's: it must beat the backdrop or it opens underneath. */}
          <JsonViewer
            value={script}
            language={rhaiLanguage}
            highlight={rhaiHighlightStyle}
            floatingZIndex={MODAL_Z_INDEX + 1}
          />
        </PreviewFrame>

        <ModalFooter>
          <ModalFooterActions>
            <ButtonMediumSecondary
              text="Cancel"
              onClick={onClose}
              disabled={form.saving}
            />
            <ButtonMediumPrimary
              text={form.saving ? "Saving…" : "Save script"}
              onClick={form.submit}
              disabled={!form.canSubmit}
            />
          </ModalFooterActions>
        </ModalFooter>
      </Card>
    </ModalBackdrop>
  );
}

// Figma Modal (demo): 600px wide.
const Card = styled(ModalCard)`
  max-width: 600px;
  width: 100%;
`;

// Figma "Text" block: 18px above the form, the modal's side padding, and
// the form-items gap below the preview heading; groups sit at the
// form-group gap.
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapgroup}px;
  padding: ${SEMANTIC.spacingl}px ${MODAL.paddingw}px ${FORM.gapitems}px;
`;

// Two equal columns at the form-group gap, each a stacked label + control.
const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${FORM.gapgroup}px;

  > * {
    flex: 1;
    min-width: 0;
  }
`;

// Figma Form/header: an H5 on a 20px row.
const PreviewHeading = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  height: 20px;
  font-size: ${H5.fontSize}px;
  font-weight: ${H5.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const ErrorLine = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme.statusError};
`;

// Figma Bridge_PipelineCode: the editor runs edge to edge under a hairline,
// capped at the mock's 250px so the modal stays within the viewport — the
// viewer scrolls inside.
const PreviewFrame = styled.div`
  border-top: 1px solid ${(p) => p.theme.semanticBorderLight};
  max-height: 250px;
  overflow: auto;
  min-height: 0;
`;
